import { z } from 'zod';

import { CoverSourceName } from './asset-source';
import { Translation } from './translation';
import { ProgressStatus } from '../enums';

const marketplaceConfig = z
  .object({
    organizationIds: z.array(z.string()).optional(),
    marketplace: z
      .object({
        enabled: z.boolean(),
        price: z.number().nonnegative().optional(),
        currency: z.string().optional(),
      })
      .optional(),
    isOrg: z.boolean().optional(),
  })
  .optional();

export const CreateEpisodeDto = z
  .object({
    language: z.string().min(1),
    youtubeUrl: z.string().url().optional().nullable(),
    documentType: z.string().optional().nullable(),
    documentSource: CoverSourceName.optional().nullable(),
    coverType: z.string().optional().nullable(),
    coverSource: CoverSourceName.optional().nullable(),
    tags: z.array(z.string()).optional(),
    translations: z.array(Translation),
    configuration: marketplaceConfig,
  })
  .refine((data) => Boolean(data.youtubeUrl) || Boolean(data.documentSource), {
    message: 'VALIDATION__EpisodeRequiresVideoOrDocument',
    path: ['youtubeUrl'],
  });

export type ApiCreateEpisodeDto = z.infer<typeof CreateEpisodeDto>;

export const UpdateEpisodeDto = z
  .object({
    language: z.string().min(1).optional(),
    youtubeUrl: z.string().url().optional().nullable(),
    documentType: z.string().optional().nullable(),
    documentSource: CoverSourceName.optional().nullable(),
    coverType: z.string().optional().nullable(),
    coverSource: CoverSourceName.optional().nullable(),
    tags: z.array(z.string()).optional(),
    translations: z.array(Translation).optional(),
    configuration: marketplaceConfig,
  })
  .strict();

export type ApiUpdateEpisodeDto = z.infer<typeof UpdateEpisodeDto>;

// ---------------------------------------------------------------------------
// Module
// ---------------------------------------------------------------------------

const moduleEpisodeItem = z.object({
  episodeId: z.string().uuid(),
  position: z.number().int().nonnegative(),
});

export const CreateModuleDto = z.object({
  translations: z.array(Translation).nonempty(),
  coverType: z.string().optional().nullable(),
  coverSource: CoverSourceName.optional().nullable(),
  documentType: z.string().optional().nullable(),
  documentSource: CoverSourceName.optional().nullable(),
  sequentialUnlock: z.boolean().optional(),
  episodes: z
    .array(moduleEpisodeItem)
    .min(1, 'VALIDATION__ModuleRequiresEpisode'),
  configuration: marketplaceConfig,
});

export type ApiCreateModuleDto = z.infer<typeof CreateModuleDto>;

export const UpdateModuleDto = z
  .object({
    translations: z.array(Translation).optional(),
    coverType: z.string().optional().nullable(),
    coverSource: CoverSourceName.optional().nullable(),
    documentType: z.string().optional().nullable(),
    documentSource: CoverSourceName.optional().nullable(),
    sequentialUnlock: z.boolean().optional(),
    episodes: z
      .array(moduleEpisodeItem)
      .min(1, 'VALIDATION__ModuleRequiresEpisode')
      .optional(),
    configuration: marketplaceConfig,
  })
  .strict();

export type ApiUpdateModuleDto = z.infer<typeof UpdateModuleDto>;

// ---------------------------------------------------------------------------
// Course
// ---------------------------------------------------------------------------

const courseModuleItem = z.object({
  moduleId: z.string().uuid(),
  position: z.number().int().nonnegative(),
});

export const CreateCourseDto = z.object({
  translations: z.array(Translation).nonempty(),
  coverType: z.string().optional().nullable(),
  coverSource: CoverSourceName.optional().nullable(),
  documentType: z.string().optional().nullable(),
  documentSource: CoverSourceName.optional().nullable(),
  sequentialUnlock: z.boolean().optional(),
  modules: z.array(courseModuleItem).min(1, 'VALIDATION__CourseRequiresModule'),
  configuration: marketplaceConfig,
});

export type ApiCreateCourseDto = z.infer<typeof CreateCourseDto>;

export const UpdateCourseDto = z
  .object({
    translations: z.array(Translation).optional(),
    coverType: z.string().optional().nullable(),
    coverSource: CoverSourceName.optional().nullable(),
    documentType: z.string().optional().nullable(),
    documentSource: CoverSourceName.optional().nullable(),
    sequentialUnlock: z.boolean().optional(),
    modules: z
      .array(courseModuleItem)
      .min(1, 'VALIDATION__CourseRequiresModule')
      .optional(),
    configuration: marketplaceConfig,
  })
  .strict();

export type ApiUpdateCourseDto = z.infer<typeof UpdateCourseDto>;

// ---------------------------------------------------------------------------
// Episode progress
// ---------------------------------------------------------------------------

// Progress is scoped to the context an episode is watched in: a `moduleId`
// pins it to that episode's instance inside a module, while an omitted
// `moduleId` means the standalone (library) context. A `courseId` further
// scopes it to that module's use within a specific course, so the same module
// reused across courses tracks progress independently per course. The same
// episode watched standalone, inside a bare module, and inside a course
// therefore tracks independent states.
export const UpsertEpisodeProgressDto = z
  .object({
    status: z.nativeEnum(ProgressStatus),
    moduleId: z.string().uuid().optional(),
    courseId: z.string().uuid().optional(),
  })
  .strict();

export type ApiUpsertEpisodeProgressDto = z.infer<
  typeof UpsertEpisodeProgressDto
>;

// Batch progress lookup. Each item carries its own `moduleId` (and optional
// `courseId`) so a single call can span episodes from several modules (e.g. the
// course viewer), with an omitted `moduleId` addressing the standalone context.
export const ListEpisodeProgressDto = z
  .object({
    items: z
      .array(
        z
          .object({
            episodeId: z.string().uuid(),
            moduleId: z.string().uuid().optional(),
            courseId: z.string().uuid().optional(),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

export type ApiListEpisodeProgressDto = z.infer<typeof ListEpisodeProgressDto>;

// ---------------------------------------------------------------------------
// Aggregated completion statistics (per episode / module / course)
// ---------------------------------------------------------------------------
//
// `started`   — learners who began the content (any non-IDLE progress).
// `completed` — learners who finished it. For an episode this is a COMPLETED
//   progress row; for a module/course it means the learner completed *every*
//   episode it contains.
// `completionRate` — completed / started, in the [0, 1] range (0 when nobody
//   has started).
export const EducationStatsDto = z.object({
  started: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  completionRate: z.number().min(0).max(1),
});

export type ApiEducationStats = z.infer<typeof EducationStatsDto>;

// Per-episode entry in a detailed statistics response. `dropOffRate` is the
// share of learners who started the episode but did not complete it — the
// signal used to rank the "hardest lessons".
export const EducationEpisodeStatDto = z.object({
  episodeId: z.string(),
  name: z.string(),
  position: z.number().int().nonnegative(),
  stats: EducationStatsDto,
  dropOffRate: z.number().min(0).max(1),
});

export type ApiEducationEpisodeStat = z.infer<typeof EducationEpisodeStatDto>;

// Detailed statistics for a single episode / module / course: the aggregate
// KPI plus a per-episode breakdown ranked by drop-off (hardest first).
export const EducationStatisticsDto = z.object({
  stats: EducationStatsDto,
  episodes: z.array(EducationEpisodeStatDto),
});

export type ApiEducationStatistics = z.infer<typeof EducationStatisticsDto>;
